import { Dispatch, useEffect, useState } from 'react';
import { CustomerProfile } from 'types';
import { SnackbarProps } from 'components/Snackbar';
import { getCustomerShares } from 'api';
import { NextLink } from 'components/NextLink';
import { LazyLoader } from 'components/LazyLoader';
import { GearCard, GearCardProps } from 'components/GearCard';
import { BrandsList } from 'components/Brands';
import { composeServerErrorForSnackbar, formatNumberToLetteredValue } from 'utils/formatters';

import cx from 'classnames';
import styles from '../UserProfile.module.scss';

export type SharesTabProps = {
  profile: CustomerProfile;
  username: string;
  setSnackbarMessage: Dispatch<SnackbarProps | null>;
  toggleFavorite: (slug: string, isFavorite: boolean) => Promise<boolean>;
  onShare: (slug: string, owner: Partial<CustomerProfile>, isSold: boolean) => void;
  onComment: (slug: string) => void;
};

export const SharesTab = ({
  profile,
  username,
  setSnackbarMessage,
  toggleFavorite,
  onShare,
  onComment,
}: SharesTabProps) => {
  const [currentShares, setCurrentShares] = useState<GearCardProps[]>([]);
  const [readyToLoad, setReadyToLoad] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);

  const fetchShares = async () => {
    if (!readyToLoad) return;
    setReadyToLoad(false);
    const response = await getCustomerShares(username, currentPage + 1);
    if (response.ok && response.hits) {
      setCurrentPage((page) => page + 1);
      setTotalPages(Math.ceil(response.total / response.limit));
      setCurrentShares((previous) => {
        return [...previous, ...response.hits];
      });
    } else {
      setSnackbarMessage(composeServerErrorForSnackbar(response));
    }
    setReadyToLoad(true);
  };

  useEffect(() => {
    fetchShares().then();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.tabContentWrapper}>
      <div className={cx(styles.content, styles.contentS)}>
        <div className={cx(styles.contentBlock, styles.sidebarBrandsBlock)}>
          <div className={styles.contentBlockHeader}>
            <div className={styles.contentBlockHeaderTitle}>
              <span>Following Brands</span>
            </div>
            <NextLink href="/brands" className={cx(styles.contentViewAll, styles.tabShow)} title="View All" />
          </div>
          <div className={styles.contentBlockBody}>
            <BrandsList brands={profile.brands} type="halfed" />
            <br className={styles.tabHide} />
            <NextLink href="/brands" className={cx(styles.contentViewAll, styles.tabHide)} title="View All Brands" />
          </div>
        </div>
      </div>

      <div className={cx(styles.content, styles.contentL)}>
        <div className={styles.contentBlock}>
          <div className={cx(styles.contentBlockHeader)}>
            <div className={styles.contentBlockHeaderTitle}>
              {formatNumberToLetteredValue(profile.shares_count)} Shares
            </div>
          </div>
          <div className={styles.contentCardGreed}>
            {!!currentShares.length &&
              currentShares.map((share) => (
                <GearCard
                  key={share.id}
                  {...share}
                  toggleFavorite={toggleFavorite}
                  onShare={onShare}
                  onComment={onComment}
                />
              ))}
            {totalPages > 1 && totalPages !== currentPage && (
              <LazyLoader isActive={readyToLoad} callback={fetchShares} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
